import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../contexts/StoreProvider';
import alertify from 'alertifyjs';

const CheckoutScreen = () => {
  const { cart, getCartTotal, clearCart, regions } = useStore();
  const navigate = useNavigate();

  const [fulfillmentMethod, setFulfillmentMethod] = useState('DELIVERY');
  const [addresses, setAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [isAddingNewAddress, setIsAddingNewAddress] = useState(false);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [newAddress, setNewAddress] = useState({ title: '', address_line: '', city: '', region: '' });
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Fetch existing addresses
  React.useEffect(() => {
    const fetchAddresses = async () => {
      const token = localStorage.getItem('emba_access');
      if (token) {
        try {
          const res = await fetch(`${API_BASE}/api/store/addresses/`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (res.ok) {
            const data = await res.json();
            const addressList = Array.isArray(data) ? data : (data.results || []);
            setAddresses(addressList);
            if (addressList.length > 0) {
              setSelectedAddressId(addressList[0].id);
            } else {
              setIsAddingNewAddress(true);
            }
          }
        } catch (e) {
          console.error(e);
        }
      }
    };
    fetchAddresses();
  }, [API_BASE]);

  if (cart.length === 0) {
    navigate('/store');
    return null;
  }

  const handleCheckout = async (e) => {
    e.preventDefault();
    
    const token = localStorage.getItem('emba_access');
    const userStr = localStorage.getItem('emba_user');
    
    if (!token || !userStr) {
      alertify.error("Please login to complete checkout");
      navigate('/login?redirect=/store/checkout');
      return;
    }
    
    const user = JSON.parse(userStr);
    
    let finalShippingAddress = '';

    if (fulfillmentMethod === 'DELIVERY') {
      const selected = addresses.find(a => a.id === selectedAddressId);
      if (!selected) {
        alertify.error("Please select a shipping address or add a new one");
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const payload = { 
        fulfillment_method: fulfillmentMethod,
        return_url: `${window.location.origin}/store/payment-status` 
      };
      if (fulfillmentMethod === 'DELIVERY') {
        payload.shipping_address = selectedAddressId;
      }

      const res = await fetch(`${API_BASE}/api/store/cart/checkout/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok || res.status === 201) {
        const orderData = await res.json();
        clearCart();
        if (orderData.checkout_url) {
          localStorage.setItem('pending_hubtel_order', orderData.order_number || '');
          window.location.href = orderData.checkout_url;
        } else {
          navigate('/store'); 
          alertify.success("Order placed successfully");
        }
      } else {
        alertify.error("Failed to checkout");
        setIsSubmitting(false);
      }
    } catch (err) {
      console.error(err);
      alertify.error("An error occurred during checkout");
      setIsSubmitting(false);
    }
  };

  const handleSaveNewAddress = async () => {
    if (!newAddress.address_line.trim()) {
      alertify.error("Please enter a street address");
      return;
    }

    const token = localStorage.getItem('emba_access');
    if (!token) return;

    setIsSavingAddress(true);
    try {
      const res = await fetch(`${API_BASE}/api/store/addresses/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          title: newAddress.title || 'Other',
          address_line: newAddress.address_line,
          city: newAddress.city,
          region: newAddress.region || null
        })
      });

      if (res.ok) {
        const savedAddress = await res.json();
        setAddresses([savedAddress, ...addresses]);
        setSelectedAddressId(savedAddress.id);
        setIsAddingNewAddress(false);
        setNewAddress({ title: '', address_line: '', city: '', region: '' });
        alertify.success("Address saved successfully!");
      } else {
        alertify.error("Failed to save address");
      }
    } catch (e) {
      console.error(e);
      alertify.error("An error occurred while saving the address");
    } finally {
      setIsSavingAddress(false);
    }
  };

  const totalDeliveryFee = fulfillmentMethod === 'DELIVERY' && selectedAddressId 
    ? parseFloat(addresses.find(a => a.id === selectedAddressId)?.region_detail?.delivery_fee || 0) 
    : 0;
    
  const finalTotal = getCartTotal() + totalDeliveryFee;

  return (
    <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 max-w-[1400px] mx-auto">
      
      {/* Checkout Form */}
      <div className="flex-1">
        <span className="text-[9px] tracking-[0.3em] uppercase text-[#13110E]/60 block mb-2">
          STEP TWO OF TWO
        </span>
        <div className="flex justify-between items-end border-b border-[#13110E]/10 pb-6 mb-12">
          <h1 className="text-5xl md:text-7xl font-serif text-[#13110E]">Checkout</h1>
          <div className="text-[9px] tracking-[0.2em] uppercase text-[#13110E]/60 flex gap-2">
            <span>BAG</span>
            <span>/</span>
            <span className="text-[#13110E] border-b border-[#13110E] pb-0.5">CHECKOUT</span>
          </div>
        </div>
        
        <form onSubmit={handleCheckout} className="space-y-12">
          {/* Fulfillment Method */}
          <div>
            <h3 className="text-[9px] tracking-[0.3em] uppercase text-[#13110E]/60 mb-6">
              FULFILLMENT METHOD
            </h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <label className="flex-1 cursor-pointer">
                <input 
                  type="radio" 
                  className="peer sr-only"
                  name="fulfillment" 
                  value="DELIVERY" 
                  checked={fulfillmentMethod === 'DELIVERY'}
                  onChange={() => setFulfillmentMethod('DELIVERY')}
                />
                <div className={`p-6 border transition-colors h-full flex flex-col justify-center ${fulfillmentMethod === 'DELIVERY' ? 'bg-[#13110E] border-[#13110E] text-[#EBE6DD]' : 'bg-transparent border-[#13110E]/20 text-[#13110E] hover:border-[#13110E]'}`}>
                  <div className="text-[10px] tracking-[0.25em] uppercase mb-1 font-medium">Delivery</div>
                  <div className={`font-serif italic text-sm ${fulfillmentMethod === 'DELIVERY' ? 'text-[#EBE6DD]/70' : 'text-[#13110E]/70'}`}>To your address</div>
                </div>
              </label>
              <label className="flex-1 cursor-pointer">
                <input 
                  type="radio" 
                  className="peer sr-only"
                  name="fulfillment" 
                  value="PICKUP" 
                  checked={fulfillmentMethod === 'PICKUP'}
                  onChange={() => setFulfillmentMethod('PICKUP')}
                />
                <div className={`p-6 border transition-colors h-full flex flex-col justify-center ${fulfillmentMethod === 'PICKUP' ? 'bg-[#13110E] border-[#13110E] text-[#EBE6DD]' : 'bg-transparent border-[#13110E]/20 text-[#13110E] hover:border-[#13110E]'}`}>
                  <div className="text-[10px] tracking-[0.25em] uppercase mb-1 font-medium">Pickup</div>
                  <div className={`font-serif italic text-sm ${fulfillmentMethod === 'PICKUP' ? 'text-[#EBE6DD]/70' : 'text-[#13110E]/70'}`}>From the atelier</div>
                </div>
              </label>
            </div>
          </div>

          {/* Shipping Address Section */}
          {fulfillmentMethod === 'DELIVERY' && (
            <div className="animate-in fade-in slide-in-from-top-4 duration-300">
              <div className="flex justify-between items-center border-b border-[#13110E]/10 pb-4 mb-6">
                <h3 className="text-[9px] tracking-[0.3em] uppercase text-[#13110E]/60">Shipping Address</h3>
                {isAddingNewAddress ? (
                  <span className="font-serif italic text-sm text-[#13110E]/80">New address details</span>
                ) : (
                  <button 
                    type="button" 
                    onClick={() => setIsAddingNewAddress(true)}
                    className="font-serif italic text-sm text-[#13110E]/80 hover:text-[#13110E] transition-colors"
                  >
                    + Add new address
                  </button>
                )}
              </div>
              
              {!isAddingNewAddress && addresses.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                  {addresses.map(addr => (
                    <label key={addr.id} className="cursor-pointer">
                      <input 
                        type="radio" 
                        name="address" 
                        className="peer sr-only"
                        checked={selectedAddressId === addr.id}
                        onChange={() => setSelectedAddressId(addr.id)}
                      />
                      <div className={`p-5 border transition-colors ${selectedAddressId === addr.id ? 'border-[#13110E] bg-white/40' : 'border-[#13110E]/20 hover:border-[#13110E]/50'}`}>
                        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#13110E] mb-2">{addr.title}</div>
                        <div className="text-sm font-serif text-[#13110E]/80">{addr.address_line}</div>
                        {addr.city && <div className="text-sm font-serif text-[#13110E]/80">{addr.city}</div>}
                        {addr.region_detail && <div className="text-xs text-[#13110E]/60 mt-2">Delivery: ₵{addr.region_detail.delivery_fee}</div>}
                      </div>
                    </label>
                  ))}
                </div>
              )}

              {(isAddingNewAddress || addresses.length === 0) && (
                <div className="space-y-6">
                  <div>
                    <label className="text-[8px] tracking-[0.25em] uppercase text-[#13110E]/50 block mb-1">ADDRESS TITLE</label>
                    <input
                      type="text"
                      placeholder="Home, Office"
                      value={newAddress.title}
                      onChange={(e) => setNewAddress({...newAddress, title: e.target.value})}
                      className="w-full bg-transparent border-b border-[#13110E]/20 py-2 focus:outline-none focus:border-[#13110E] transition-colors font-serif text-lg text-[#13110E] placeholder:italic placeholder:text-[#13110E]/30"
                    />
                  </div>
                  
                  <div>
                    <label className="text-[8px] tracking-[0.25em] uppercase text-[#13110E]/50 block mb-1">STREET ADDRESS, SUBURB, GPS ADDRESS</label>
                    <input
                      type="text"
                      value={newAddress.address_line}
                      onChange={(e) => setNewAddress({...newAddress, address_line: e.target.value})}
                      className="w-full bg-transparent border-b border-[#13110E]/20 py-2 focus:outline-none focus:border-[#13110E] transition-colors font-serif text-lg text-[#13110E]"
                      required={fulfillmentMethod === 'DELIVERY'}
                    />
                  </div>
                  
                  <div className="flex gap-8">
                    <div className="flex-1">
                      <label className="text-[8px] tracking-[0.25em] uppercase text-[#13110E]/50 block mb-1">CITY</label>
                      <input
                        type="text"
                        value={newAddress.city}
                        onChange={(e) => setNewAddress({...newAddress, city: e.target.value})}
                        className="w-full bg-transparent border-b border-[#13110E]/20 py-2 focus:outline-none focus:border-[#13110E] transition-colors font-serif text-lg text-[#13110E]"
                      />
                    </div>
                    
                    <div className="flex-1 relative">
                      <label className="text-[8px] tracking-[0.25em] uppercase text-[#13110E]/50 block mb-1">REGION</label>
                      <select
                        value={newAddress.region}
                        onChange={(e) => setNewAddress({...newAddress, region: e.target.value})}
                        className="w-full bg-transparent border-b border-[#13110E]/20 py-2 focus:outline-none focus:border-[#13110E] transition-colors font-serif text-lg text-[#13110E] appearance-none rounded-none"
                        required={fulfillmentMethod === 'DELIVERY'}
                      >
                        <option value="" className="font-sans text-sm">Select region</option>
                        {regions && regions.map(r => (
                          <option key={r.id} value={r.id} className="font-sans text-sm">{r.name}</option>
                        ))}
                      </select>
                      <div className="absolute right-0 bottom-3 pointer-events-none text-[#13110E]/50">
                        <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.2"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6 pt-4">
                    <button 
                      type="button"
                      onClick={handleSaveNewAddress}
                      disabled={isSavingAddress}
                      className="text-[9px] tracking-[0.3em] uppercase text-[#13110E] border-b border-[#13110E] pb-1 hover:opacity-60 transition-opacity disabled:opacity-30"
                    >
                      {isSavingAddress ? 'SAVING...' : 'SAVE ADDRESS'}
                    </button>
                    {addresses.length > 0 && (
                      <button 
                        type="button"
                        onClick={() => setIsAddingNewAddress(false)}
                        className="text-[9px] tracking-[0.3em] uppercase text-[#13110E]/50 hover:text-[#13110E] transition-colors"
                      >
                        CANCEL
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {fulfillmentMethod === 'PICKUP' && (
            <div className="animate-in fade-in slide-in-from-top-4 duration-300 border-t border-[#13110E]/10 pt-6">
              <h3 className="text-[9px] tracking-[0.3em] uppercase text-[#13110E]/60 mb-2">Pickup Location</h3>
              <p className="font-serif italic text-lg text-[#13110E]/80">Available at EMBA premises</p>
            </div>
          )}

          <div className="pt-6 border-t border-[#13110E]/10">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#13110E] text-[#EBE6DD] py-5 flex items-center justify-center gap-4 text-[10px] tracking-[0.3em] font-medium hover:bg-[#13110E]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>{isSubmitting ? 'PROCESSING...' : 'PAY & COMPLETE ORDER'}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </button>
          </div>
        </form>
      </div>

      {/* Cart Summary */}
      <div className="w-full lg:w-[400px] flex-shrink-0">
        <div className="bg-[#13110E] text-[#EBE6DD] p-8 lg:p-10 sticky top-24">
          <h2 className="text-[9px] tracking-[0.3em] uppercase text-[#EBE6DD]/60 border-b border-[#EBE6DD]/20 pb-4 mb-8">
            YOUR ORDER
          </h2>
          
          <div className="space-y-6 max-h-[50vh] overflow-y-auto pr-2 scrollbar-hide mb-8">
            {cart.map((item, index) => (
              <div key={`${item.product.id}-${index}`} className="flex gap-4 border-b border-[#EBE6DD]/10 pb-6 last:border-0 last:pb-0">
                <div className="w-16 h-20 aspect-[4/5] bg-[#E5DFD5]/10 flex-shrink-0">
                  {item.product.images && item.product.images.length > 0 ? (
                    <img src={item.product.images[0].image} alt={item.product.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#EBE6DD]/30 text-[8px] font-serif italic">No Img</div>
                  )}
                </div>
                
                <div className="flex-1 flex flex-col justify-center min-w-0">
                  <div className="text-[8px] tracking-[0.2em] text-[#EBE6DD]/60 uppercase mb-1">
                    {item.product.category_detail?.name || 'AFRICAN WEAR'}
                  </div>
                  <div className="font-serif text-base text-[#EBE6DD] truncate mb-1">
                    {item.product.name}
                  </div>
                  <div className="text-[11px] font-sans text-[#EBE6DD]/70">
                    {item.variant_detail ? `Size ${item.variant_detail.size}` : 'Standard'} • Qty {item.quantity}
                  </div>
                </div>
                
                <div className="text-sm font-serif text-[#EBE6DD] flex items-center">
                  ₵{((item.variant_detail ? parseFloat(item.variant_detail.price) : parseFloat(item.product.price)) * item.quantity).toFixed(2)}
                </div>
              </div>
            ))}
          </div>

          <div className="border-t border-[#EBE6DD]/20 pt-6 space-y-4">
            <div className="flex justify-between items-center text-[10px] tracking-[0.2em] uppercase text-[#EBE6DD]/80">
              <span>SUBTOTAL</span>
              <span className="font-sans tracking-normal">₵{getCartTotal().toFixed(2)}</span>
            </div>
            
            {fulfillmentMethod === 'DELIVERY' && selectedAddressId && (
              <div className="flex justify-between items-center text-[10px] tracking-[0.2em] uppercase text-[#EBE6DD]/80">
                <span>DELIVERY FEE</span>
                <span className="font-sans tracking-normal">
                  ₵{totalDeliveryFee.toFixed(2)}
                </span>
              </div>
            )}
            
            <div className="flex justify-between items-end pt-8">
              <span className="text-[9px] tracking-[0.3em] uppercase text-[#EBE6DD]/60 pb-2">TOTAL</span>
              <span className="text-3xl md:text-4xl font-serif text-[#EBE6DD]">
                ₵{finalTotal.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default CheckoutScreen;
